import { useState } from "react";
import {
  Search,
  Plus,
  Wrench,
  Clock,
  CheckCircle2,
  AlertOctagon,
  X,
  Trash2,
  Calendar,
  Gauge,
} from "lucide-react";

const initialRequests = [
  {
    id: "MNT-3001",
    equipment: "CNC Lathe Machine #2",
    type: "Preventive",
    priority: "Medium",
    status: "Scheduled",
    technician: "Ramesh Naidu",
    centre: "APIC Visakhapatnam",
    date: "2026-08-02",
    notes: "Quarterly lubrication and spindle alignment check.",
  },
  {
    id: "MNT-3002",
    equipment: "Drone Test Rig",
    type: "Corrective",
    priority: "High",
    status: "In Progress",
    technician: "Priya Kumari",
    centre: "APIC Vijayawada",
    date: "2026-07-26",
    notes: "Motor mount vibration reported by QA team.",
  },
  {
    id: "MNT-3003",
    equipment: "3D Printer (Resin)",
    type: "Emergency",
    priority: "Critical",
    status: "Overdue",
    technician: "Suresh Babu",
    centre: "APIC Tirupati",
    date: "2026-07-20",
    notes: "Resin leak damaging print bed heater.",
  },
  {
    id: "MNT-3004",
    equipment: "Robotic Arm — Gripper Cell",
    type: "Preventive",
    priority: "Low",
    status: "Completed",
    technician: "Vikram Rao",
    centre: "APIC Kurnool",
    date: "2026-07-15",
    notes: "Routine joint calibration completed.",
  },
  {
    id: "MNT-3005",
    equipment: "PCB Reflow Oven",
    type: "Corrective",
    priority: "Medium",
    status: "In Progress",
    technician: "Anita Reddy",
    centre: "APIC Tirupati",
    date: "2026-07-27",
    notes: "Temperature sensor drifting by ~8°C.",
  },
  {
    id: "MNT-3006",
    equipment: "Air Compressor Unit",
    type: "Preventive",
    priority: "Low",
    status: "Scheduled",
    technician: "Lakshmi Devi",
    centre: "APIC Kurnool",
    date: "2026-08-05",
    notes: "Filter replacement and pressure test.",
  },
];

const TYPES = ["Preventive", "Corrective", "Emergency"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_FILTERS = ["All", "Scheduled", "In Progress", "Completed", "Overdue"];
const STATUS_OPTIONS = ["Scheduled", "In Progress", "Completed", "Overdue"];

const STATUS_STYLES = {
  Scheduled: "bg-sky-100 text-sky-700",
  "In Progress": "bg-orange-100 text-orange-700",
  Completed: "bg-green-100 text-green-700",
  Overdue: "bg-red-100 text-red-700",
};

const STATUS_ICONS = {
  Scheduled: <Calendar size={16} />,
  "In Progress": <Gauge size={16} />,
  Completed: <CheckCircle2 size={16} />,
  Overdue: <AlertOctagon size={16} />,
};

const PRIORITY_STYLES = {
  Low: "bg-slate-100 text-slate-600",
  Medium: "bg-amber-100 text-amber-700",
  High: "bg-orange-100 text-orange-700",
  Critical: "bg-red-100 text-red-700",
};

function StatCard({ icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900 leading-tight">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children, small }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
      <div
        className={`bg-white w-full ${
          small ? "sm:max-w-xs" : "sm:max-w-md"
        } sm:rounded-2xl rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function formatDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MaintenanceScreen() {
  const [requests, setRequests] = useState(initialRequests);
  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // id of request whose status popup is open

  const [newReq, setNewReq] = useState({
    equipment: "",
    type: TYPES[0],
    priority: PRIORITIES[0],
    technician: "",
    centre: "",
    date: "",
    notes: "",
  });

  const term = search.trim().toLowerCase();

  const filtered = requests.filter((r) => {
    const matchesStatus = activeStatus === "All" || r.status === activeStatus;
    const matchesSearch =
      !term ||
      r.equipment.toLowerCase().includes(term) ||
      r.id.toLowerCase().includes(term) ||
      r.technician.toLowerCase().includes(term) ||
      r.centre.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const total = requests.length;
  const inProgress = requests.filter((r) => r.status === "In Progress").length;
  const overdue = requests.filter((r) => r.status === "Overdue").length;
  const completed = requests.filter((r) => r.status === "Completed").length;

  const targetRequest = requests.find((r) => r.id === statusTarget) || null;

  function handleAddRequest(e) {
    e.preventDefault();
    if (!newReq.equipment || !newReq.technician || !newReq.centre || !newReq.date) return;
    const id = "MNT-" + (3000 + requests.length + 1);
    setRequests([
      ...requests,
      {
        id,
        equipment: newReq.equipment,
        type: newReq.type,
        priority: newReq.priority,
        status: "Scheduled",
        technician: newReq.technician,
        centre: newReq.centre,
        date: newReq.date,
        notes: newReq.notes,
      },
    ]);
    setNewReq({ equipment: "", type: TYPES[0], priority: PRIORITIES[0], technician: "", centre: "", date: "", notes: "" });
    setShowAddModal(false);
  }

  function handleDelete(id) {
    setRequests((prev) => prev.filter((r) => r.id !== id));
  }

  function applyStatus(id, status) {
    setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    setStatusTarget(null);
  }

  return (
    <div className="" >
      <div className="max-w-6xl mx-auto" style={{height:"90vh",overflow:'auto',scrollbarWidth:'none'}}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1">
              Maintenance
            </h1>
            <p className="text-slate-500">{total} requests across 4 APIC centres</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 bg-gradient-to-br from-indigo-500 to-blue-500 text-white font-semibold px-5 py-3 rounded-xl shadow-sm hover:opacity-95 whitespace-nowrap"
          >
            <Plus size={18} />
            New Request
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Wrench size={20} className="text-blue-600" />}
            label="Total Requests"
            value={total}
            tone="bg-blue-50"
          />
          <StatCard
            icon={<Gauge size={20} className="text-orange-600" />}
            label="In Progress"
            value={inProgress}
            tone="bg-orange-50"
          />
          <StatCard
            icon={<AlertOctagon size={20} className="text-red-600" />}
            label="Overdue"
            value={overdue}
            tone="bg-red-50"
          />
          <StatCard
            icon={<CheckCircle2 size={20} className="text-green-600" />}
            label="Completed"
            value={completed}
            tone="bg-green-50"
          />
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-3 mb-4">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search equipment, ID, technician, or centre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((s) => {
              const count = s === "All" ? requests.length : requests.filter((r) => r.status === s).length;
              const active = activeStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => setActiveStatus(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-gradient-to-br from-indigo-500 to-blue-500 text-white border-transparent"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {s} <span className="opacity-75 font-bold text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4 px-1">
          Showing {filtered.length} of {requests.length} requests
        </p>

        {/* Desktop table */}
        <div className="hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
                <th className="px-5 py-3 font-semibold">Ticket ID</th>
                <th className="px-5 py-3 font-semibold">Equipment</th>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Priority</th>
                <th className="px-5 py-3 font-semibold">Technician</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-14 text-slate-500">
                    No maintenance requests found. Try a different search or filter.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-4 font-mono text-xs text-slate-600">{r.id}</td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-900">{r.equipment}</p>
                      <p className="text-xs text-slate-500">{r.centre}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{r.type}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${PRIORITY_STYLES[r.priority]}`}>
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{r.technician}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setStatusTarget(r.id)}
                          className="flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50 whitespace-nowrap"
                        >
                          <Clock size={13} />
                          Update
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="text-slate-400 hover:text-red-600 p-1.5"
                          title="Delete request"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden flex flex-col gap-4">
          {filtered.length === 0 ? (
            <div className="text-center py-14 px-5 bg-white border border-dashed border-slate-200 rounded-2xl text-slate-500">
              No maintenance requests found. Try a different search or filter.
            </div>
          ) : (
            filtered.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <span className="font-mono text-xs border border-slate-200 rounded-full px-3 py-1 text-slate-600">
                    {r.id}
                  </span>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLES[r.status]}`}>
                    {r.status}
                  </span>
                </div>
                <p className="text-lg font-extrabold text-slate-900">{r.equipment}</p>
                <p className="text-sm text-slate-500 mb-3">
                  {r.type} · {r.centre}
                </p>

                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-500">Priority</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${PRIORITY_STYLES[r.priority]}`}>
                    {r.priority}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-500">Technician</span>
                  <span className="font-semibold text-slate-900">{r.technician}</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-slate-500 flex items-center gap-1">
                    <Calendar size={13} /> Scheduled
                  </span>
                  <span className="font-semibold text-slate-900">{formatDate(r.date)}</span>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                  <button
                    onClick={() => setStatusTarget(r.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50"
                  >
                    <Clock size={14} />
                    Update Status
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    className="text-slate-400 hover:text-red-600 p-2 border border-slate-200 rounded-lg"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Request Modal */}
      {showAddModal && (
        <Modal title="New Maintenance Request" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddRequest} className="flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Equipment / Machine</label>
              <input
                type="text"
                value={newReq.equipment}
                onChange={(e) => setNewReq({ ...newReq, equipment: e.target.value })}
                placeholder="e.g. CNC Lathe Machine #3"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Type</label>
                <select
                  value={newReq.type}
                  onChange={(e) => setNewReq({ ...newReq, type: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Priority</label>
                <select
                  value={newReq.priority}
                  onChange={(e) => setNewReq({ ...newReq, priority: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Assigned Technician</label>
              <input
                type="text"
                value={newReq.technician}
                onChange={(e) => setNewReq({ ...newReq, technician: e.target.value })}
                placeholder="e.g. Ramesh Naidu"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">APIC Centre</label>
                <input
                  type="text"
                  value={newReq.centre}
                  onChange={(e) => setNewReq({ ...newReq, centre: e.target.value })}
                  placeholder="e.g. APIC Kurnool"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Scheduled Date</label>
                <input
                  type="date"
                  value={newReq.date}
                  onChange={(e) => setNewReq({ ...newReq, date: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Notes</label>
              <textarea
                value={newReq.notes}
                onChange={(e) => setNewReq({ ...newReq, notes: e.target.value })}
                placeholder="Describe the issue or task..."
                rows={3}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400 resize-none"
              />
            </div>

            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-slate-200 rounded-lg py-2.5 font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-br from-indigo-500 to-blue-500 text-white rounded-lg py-2.5 font-semibold hover:opacity-95"
              >
                Create Request
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Update Status Popup */}
      {statusTarget && targetRequest && (
        <Modal title="Update Status" onClose={() => setStatusTarget(null)} small>
          <p className="text-sm text-slate-500 mb-4">
            <span className="font-semibold text-slate-900">{targetRequest.equipment}</span>
            <br />
            <span className="font-mono text-xs">{targetRequest.id}</span>
          </p>
          <div className="flex flex-col gap-2">
            {STATUS_OPTIONS.map((s) => {
              const isCurrent = targetRequest.status === s;
              return (
                <button
                  key={s}
                  onClick={() => applyStatus(targetRequest.id, s)}
                  className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                    isCurrent
                      ? `${STATUS_STYLES[s]} border-transparent`
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {STATUS_ICONS[s]}
                    {s}
                  </span>
                  {isCurrent && <CheckCircle2 size={16} />}
                </button>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}